/*
 * Manual form record helpers and UI controller.
 * Dependency-free helpers are exported for node:test and attached to the
 * browser so the NexBus config page can use the same validation rules.
 */
(function attachManualForm(globalScope, factory) {
  const api = factory()

  if (typeof module === 'object' && module.exports) module.exports = api
  if (globalScope) globalScope.ManualForm = api
})(typeof globalThis !== 'undefined' ? globalThis : this, function createManualForm() {
  'use strict'

  const SUPPORTED_TYPES = Object.freeze([
    'Real', 'Float', 'Double', 'Int', 'DInt', 'Word', 'DWord', 'Int64', 'Bool', 'String'
  ])
  const NUMERIC_TYPES = new Set(['Real', 'Float', 'Double', 'Int', 'DInt', 'Word', 'DWord', 'Int64'])
  const INTEGER_TYPES = new Set(['Int', 'DInt', 'Word', 'DWord', 'Int64'])

  function isManualForm(form) {
    return Boolean(form && form.bus_type === 'Manual' && form.source_type === 'manual')
  }

  function prepareImportForm(document) {
    if (!document || typeof document !== 'object' || Array.isArray(document)) {
      throw new Error('JSON dosyası bir form nesnesi içermelidir.')
    }

    const formId = Number(document.form_id)
    if (!Number.isInteger(formId) || formId <= 0) {
      throw new Error('Manual form_id pozitif bir tam sayı olmalıdır.')
    }
    if (!isManualForm(document)) {
      throw new Error('Yalnız bus_type "Manual" ve source_type "manual" olan form dosyaları içe aktarılabilir.')
    }
    if (!Array.isArray(document.tags) || document.tags.length === 0) {
      throw new Error('Manuel form için en az bir tag bulunmalıdır.')
    }

    const tagNames = new Set()
    const tags = document.tags.map((tag, index) => {
      const name = String(tag && tag.name || '').trim()
      const dataType = String(tag && tag.data_type || '').trim()
      if (!name) throw new Error(`${index + 1}. manuel tag adı boş olamaz.`)
      if (!SUPPORTED_TYPES.includes(dataType)) {
        throw new Error(`${name} için desteklenmeyen veri tipi: ${dataType || 'boş'}`)
      }
      const foldedName = name.toLocaleLowerCase('tr-TR')
      if (tagNames.has(foldedName)) throw new Error(`Manuel tag adı tekrar ediyor: ${name}`)
      tagNames.add(foldedName)
      return {
        name,
        data_type: dataType,
        description: String(tag && tag.description || '').trim(),
        unit: String(tag && tag.unit || '').trim(),
        keep_minute_history: false
      }
    })

    return {
      form_id: formId,
      ip_address: 'manual',
      bus_type: 'Manual',
      source_type: 'manual',
      enabled: document.enabled !== false,
      description: String(document.description || ''),
      tags
    }
  }

  function createIdempotencyKey() {
    const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : null
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
    if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
      const bytes = cryptoApi.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, token => {
      const random = Math.floor(Math.random() * 16)
      return (token === 'x' ? random : ((random & 0x3) | 0x8)).toString(16)
    })
  }

  function localDateTimeValue(date = new Date()) {
    const pad = value => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  }

  function localDateTimeToOffsetIso(value) {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (!match) throw new Error('Geçerli bir kayıt tarihi ve saati seçin.')
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6] || 0), 0)
    if (Number.isNaN(date.getTime())) throw new Error('Geçerli bir kayıt tarihi ve saati seçin.')
    const offsetMinutes = -date.getTimezoneOffset()
    const sign = offsetMinutes >= 0 ? '+' : '-'
    const absolute = Math.abs(offsetMinutes)
    const pad = input => String(input).padStart(2, '0')
    return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${pad(match[6] || 0)}${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`
  }

  function isoToLocalDateTimeValue(value) {
    if (!value) return localDateTimeValue()
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? localDateTimeValue() : localDateTimeValue(date)
  }

  function coerceValue(dataType, rawValue) {
    const type = String(dataType || '')
    if (!SUPPORTED_TYPES.includes(type)) throw new Error(`Desteklenmeyen veri tipi: ${type || 'boş'}`)
    if (type === 'String') return String(rawValue == null ? '' : rawValue)
    if (type === 'Bool') {
      if (rawValue === true || rawValue === 'true' || rawValue === 1 || rawValue === '1') return true
      if (rawValue === false || rawValue === 'false' || rawValue === 0 || rawValue === '0') return false
      throw new Error('Bool değeri true veya false olmalıdır.')
    }
    const value = Number(rawValue)
    if (!Number.isFinite(value)) throw new Error(`${type} için geçerli bir sayı girin.`)
    if (INTEGER_TYPES.has(type) && !Number.isInteger(value)) throw new Error(`${type} için tam sayı girin.`)
    return value
  }

  function stableStringify(value) {
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
    }
    return JSON.stringify(value)
  }

  class IdempotencyTracker {
    constructor(keyFactory = createIdempotencyKey) {
      this.keyFactory = keyFactory
      this.signature = null
      this.key = null
    }

    keyFor(payload) {
      const signature = stableStringify(payload)
      if (!this.key || signature !== this.signature) {
        this.signature = signature
        this.key = this.keyFactory()
      }
      return this.key
    }

    clear() {
      this.signature = null
      this.key = null
    }
  }

  function errorMessage(status, payload) {
    const detail = payload && (payload.message || payload.detail || payload.error_description || (typeof payload.error === 'string' ? payload.error : ''))
    if (detail) return String(detail)
    const messages = {
      400: 'Geçersiz alan veya veri tipi gönderildi.',
      401: 'Oturum gerekli veya oturum anahtarı geçersiz.',
      403: 'Bu kaydı düzenleme veya silme yetkiniz yok.',
      404: 'Form veya manuel kayıt bulunamadı.',
      409: 'Form manuel değil veya aynı Idempotency-Key farklı bir kayıt için kullanıldı.',
      503: 'Manuel kayıt depolama servisi geçici olarak kullanılamıyor.'
    }
    return messages[status] || `İstek başarısız oldu (HTTP ${status}).`
  }

  function recordValues(record) {
    const source = record && (record.values || record.tag_values || record.data)
    if (Array.isArray(source)) {
      return source.reduce((result, item) => {
        const name = item && (item.tag_name || item.name)
        if (name) result[name] = item.value
        return result
      }, {})
    }
    return source && typeof source === 'object' ? { ...source } : {}
  }

  function normalizeListResponse(payload, fallbackLimit, fallbackOffset) {
    const source = payload && typeof payload === 'object' ? payload : {}
    const nested = source.data && !Array.isArray(source.data) && typeof source.data === 'object' ? source.data : {}
    const records = Array.isArray(source.records)
      ? source.records
      : (Array.isArray(source.items)
          ? source.items
          : (Array.isArray(source.data)
              ? source.data
              : (Array.isArray(nested.records) ? nested.records : (Array.isArray(nested.items) ? nested.items : (Array.isArray(payload) ? payload : [])))))
    return {
      records,
      total: Number.isFinite(Number(source.total ?? nested.total)) ? Number(source.total ?? nested.total) : records.length,
      limit: Number.isFinite(Number(source.limit ?? nested.limit)) ? Number(source.limit ?? nested.limit) : fallbackLimit,
      offset: Number.isFinite(Number(source.offset ?? nested.offset)) ? Number(source.offset ?? nested.offset) : fallbackOffset
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  async function parseResponse(response) {
    const text = await response.text()
    if (!text) return null
    try { return JSON.parse(text) } catch (_) { return { message: text } }
  }

  function unwrapRecord(payload) {
    if (!payload || typeof payload !== 'object') return payload
    const source = payload.record || payload.data || payload
    return source && source.record ? source.record : source
  }

  function getRecordId(record) {
    return record && (record.record_id ?? record.id ?? record.manual_record_id)
  }

  function latestRecord(records) {
    const source = Array.isArray(records) ? records : []
    return source.reduce((latest, record) => {
      if (!latest) return record
      const currentTime = Date.parse(record && (record.time || record.record_time || record.created_at))
      const latestTime = Date.parse(latest && (latest.time || latest.record_time || latest.created_at))
      if (Number.isFinite(currentTime) && (!Number.isFinite(latestTime) || currentTime > latestTime)) return record
      return latest
    }, null)
  }

  class RecordsController {
    constructor(options = {}) {
      this.root = document.getElementById(options.rootId || 'manualRecordsSection')
      this.showToast = options.showToast || (() => {})
      this.onLiveRefresh = options.onLiveRefresh || (() => {})
      this.onLatestValues = options.onLatestValues || (() => {})
      this.request = options.request || ((url, requestOptions) => {
        if (typeof window !== 'undefined' && window.Auth && typeof window.Auth.authenticatedFetch === 'function') {
          return window.Auth.authenticatedFetch(url, requestOptions)
        }
        return fetch(url, requestOptions)
      })
      this.form = null
      this.limit = 50
      this.offset = 0
      this.total = 0
      this.records = []
      this.submitting = false
      this.loadSequence = 0
      this.idempotency = new IdempotencyTracker()
      this.detailRecord = null
      this.bindEvents()
    }

    bindEvents() {
      const submit = document.getElementById('manualRecordSubmit')
      if (submit) submit.addEventListener('click', event => this.submit(event))
      const open = document.getElementById('manualRecordsOpen')
      if (open) open.addEventListener('click', () => this.open())
      const refresh = document.getElementById('manualRecordsRefresh')
      if (refresh) refresh.addEventListener('click', () => this.loadRecords())
      const previous = document.getElementById('manualRecordsPrevious')
      const next = document.getElementById('manualRecordsNext')
      if (previous) previous.addEventListener('click', () => this.changePage(-1))
      if (next) next.addEventListener('click', () => this.changePage(1))
      const save = document.getElementById('manualRecordEditSave')
      if (save) save.addEventListener('click', () => this.saveEdit())
    }

    open() {
      if (!this.form) return
      const modalElement = document.getElementById('manualRecordsModal')
      if (modalElement && typeof coreui !== 'undefined' && coreui.Modal) {
        coreui.Modal.getOrCreateInstance(modalElement).show()
      }
      this.loadRecords()
    }

    setForm(form) {
      this.form = isManualForm(form) ? form : null
      this.idempotency.clear()
      this.offset = 0
      if (!this.root) return
      this.root.style.display = this.form ? 'block' : 'none'
      if (!this.form) {
        this.loadSequence += 1
        return
      }
      const time = document.getElementById('manualRecordTime')
      if (time) time.value = localDateTimeValue()
      this.renderEntryFields()
      this.loadRecords()
    }

    renderEntryFields() {
      const container = document.getElementById('manualRecordFields')
      if (!container || !this.form) return
      const tags = Array.isArray(this.form.tags) ? this.form.tags : []
      if (!tags.length) {
        container.innerHTML = '<div class="alert alert-warning mb-0">Kayıt girişi için önce en az bir manuel tag tanımlayın ve formu deploy edin.</div>'
        return
      }
      container.innerHTML = tags.map((tag, index) => {
        const type = SUPPORTED_TYPES.includes(tag.data_type) ? tag.data_type : 'String'
        const control = type === 'Bool'
          ? `<select class="form-select manual-value-input" data-tag-index="${index}"><option value="true">true</option><option value="false">false</option></select>`
          : `<input class="form-control manual-value-input" data-tag-index="${index}" type="${NUMERIC_TYPES.has(type) ? 'number' : 'text'}" ${NUMERIC_TYPES.has(type) ? 'step="any"' : ''} autocomplete="off">`
        return `<div class="col-lg-6">
          <div class="input-group">
            <span class="input-group-text"><input class="form-check-input mt-0 manual-value-enabled" data-tag-index="${index}" type="checkbox" aria-label="${escapeHtml(tag.name)} değerini gönder"></span>
            <span class="input-group-text text-start" style="min-width:170px"><span><strong>${escapeHtml(tag.name)}</strong><br><small class="text-white-50">${escapeHtml(type)}${tag.description ? ` — ${escapeHtml(tag.description)}` : ''}</small></span></span>
            ${control}
            ${tag.unit ? `<span class="input-group-text">${escapeHtml(tag.unit)}</span>` : ''}
          </div>
        </div>`
      }).join('')
      container.querySelectorAll('.manual-value-input').forEach(input => {
        input.addEventListener('input', () => {
          const checkbox = container.querySelector(`.manual-value-enabled[data-tag-index="${input.dataset.tagIndex}"]`)
          if (checkbox && String(input.value).length > 0) checkbox.checked = true
        })
      })
    }

    buildCreatePayload() {
      if (!this.form) throw new Error('Manuel form seçili değil.')
      const tags = Array.isArray(this.form.tags) ? this.form.tags : []
      const values = []
      document.querySelectorAll('#manualRecordFields .manual-value-enabled:checked').forEach(checkbox => {
        const index = Number(checkbox.dataset.tagIndex)
        const tag = tags[index]
        const input = document.querySelector(`#manualRecordFields .manual-value-input[data-tag-index="${index}"]`)
        if (!tag || !input) return
        if (tag.data_type !== 'Bool' && String(input.value).trim() === '') throw new Error(`${tag.name} için değer girin.`)
        values.push({ tag_name: tag.name, value: coerceValue(tag.data_type, input.value) })
      })
      if (!values.length) throw new Error('Göndermek için en az bir tag değeri seçin.')
      return {
        time: localDateTimeToOffsetIso(document.getElementById('manualRecordTime').value),
        values
      }
    }

    async submit(event) {
      event.preventDefault()
      if (this.submitting || !this.form) return
      let payload
      try { payload = this.buildCreatePayload() } catch (error) {
        this.showToast('Manuel Kayıt', error.message, 'warning')
        return
      }
      const key = this.idempotency.keyFor(payload)
      const button = document.getElementById('manualRecordSubmit')
      this.submitting = true
      if (button) {
        button.disabled = true
        button.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Gönderiliyor'
      }
      try {
        const response = await this.request(`/api/v1/forms/${encodeURIComponent(this.form.form_id)}/manual-records`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'Idempotency-Key': key },
          body: JSON.stringify(payload)
        })
        const result = await parseResponse(response)
        if (!response.ok) throw Object.assign(new Error(errorMessage(response.status, result)), { status: response.status })
        this.idempotency.clear()
        document.querySelectorAll('#manualRecordFields .manual-value-enabled').forEach(input => { input.checked = false })
        document.querySelectorAll('#manualRecordFields .manual-value-input').forEach(input => { if (input.tagName !== 'SELECT') input.value = '' })
        document.getElementById('manualRecordTime').value = localDateTimeValue()
        const createdRecord = unwrapRecord(result) || {}
        const replay = Boolean((result && result.idempotent_replay) || createdRecord.idempotent_replay)
        const pending = createdRecord.publication_status === 'pending'
        this.showToast(
          replay ? 'Kayıt daha önce alınmıştı' : 'Manuel kayıt alındı',
          pending ? 'Kayıt alındı, canlı yayın sıraya alındı.' : (replay ? 'Aynı kayıt yeniden oluşturulmadı.' : 'Kayıt başarıyla oluşturuldu.'),
          pending ? 'info' : 'success'
        )
        await this.loadRecords()
        this.onLiveRefresh(this.form.form_id)
      } catch (error) {
        this.showToast('Manuel Kayıt Hatası', error.message || 'Kayıt gönderilemedi.', 'danger')
      } finally {
        this.submitting = false
        if (button) {
          button.disabled = false
          button.innerHTML = '<i class="fas fa-paper-plane me-1"></i>Kaydı Gönder'
        }
      }
    }

    async loadRecords() {
      if (!this.form) return
      const sequence = ++this.loadSequence
      const body = document.getElementById('manualRecordsBody')
      if (body) body.innerHTML = '<tr><td colspan="7" class="text-center py-4"><span class="spinner-border spinner-border-sm me-2"></span>Kayıtlar yükleniyor...</td></tr>'
      try {
        const response = await this.request(`/api/v1/forms/${encodeURIComponent(this.form.form_id)}/manual-records?limit=${this.limit}&offset=${this.offset}`, { headers: { Accept: 'application/json' } })
        const payload = await parseResponse(response)
        if (!response.ok) throw new Error(errorMessage(response.status, payload))
        if (sequence !== this.loadSequence) return
        const normalized = normalizeListResponse(payload, this.limit, this.offset)
        this.records = normalized.records
        this.total = normalized.total
        this.limit = normalized.limit || 50
        this.offset = normalized.offset
        const latest = latestRecord(this.records)
        this.onLatestValues(latest ? recordValues(latest) : {}, latest)
        this.renderRecords()
      } catch (error) {
        if (sequence !== this.loadSequence) return
        if (body) body.innerHTML = `<tr><td colspan="7" class="text-center text-danger py-4">${escapeHtml(error.message)}</td></tr>`
        this.renderPagination()
      }
    }

    renderRecords() {
      const body = document.getElementById('manualRecordsBody')
      if (!body) return
      if (!this.records.length) {
        body.innerHTML = '<tr><td colspan="7" class="text-center text-white-50 py-4">Henüz manuel kayıt yok.</td></tr>'
        this.renderPagination()
        return
      }
      body.innerHTML = this.records.map(record => {
        const id = getRecordId(record)
        const values = recordValues(record)
        const valueHtml = Object.entries(values).map(([name, value]) => `<span class="badge bg-secondary me-1 mb-1">${escapeHtml(name)}: ${escapeHtml(value)}</span>`).join('') || '—'
        const createdBy = record.created_by_name || record.creator_name || record.username || record.created_by_user_id || '—'
        const canEdit = this.canManageRecord(record, 'edit')
        const canDelete = this.canManageRecord(record, 'delete')
        return `<tr>
          <td>${escapeHtml(record.time || record.record_time || '—')}</td>
          <td>${valueHtml}</td>
          <td>${escapeHtml(createdBy)}</td>
          <td>${escapeHtml(record.created_at || '—')}</td>
          <td>${escapeHtml(record.revision ?? '—')}</td>
          <td><button class="btn btn-sm btn-outline-info manual-record-detail" data-record-id="${escapeHtml(id)}" type="button"><i class="fas fa-eye"></i></button></td>
          <td class="text-nowrap">
            ${canEdit ? `<button class="btn btn-sm btn-outline-warning me-1 manual-record-edit" data-record-id="${escapeHtml(id)}" type="button" title="Düzenle"><i class="fas fa-edit"></i></button>` : ''}
            ${canDelete ? `<button class="btn btn-sm btn-outline-danger manual-record-delete" data-record-id="${escapeHtml(id)}" type="button" title="Sil"><i class="fas fa-trash"></i></button>` : ''}
          </td>
        </tr>`
      }).join('')
      body.querySelectorAll('.manual-record-detail').forEach(button => button.addEventListener('click', () => this.openDetail(button.dataset.recordId, false)))
      body.querySelectorAll('.manual-record-edit').forEach(button => button.addEventListener('click', () => this.openDetail(button.dataset.recordId, true)))
      body.querySelectorAll('.manual-record-delete').forEach(button => button.addEventListener('click', () => this.deleteRecord(button.dataset.recordId)))
      this.renderPagination()
    }

    canManageRecord(record, operation) {
      const capability = operation === 'delete' ? record.can_delete : record.can_edit
      if (typeof capability === 'boolean') return capability
      if (typeof window !== 'undefined' && window.Auth && typeof window.Auth.hasRole === 'function' && window.Auth.hasRole('admin')) return true
      const user = typeof window !== 'undefined' && window.Auth && typeof window.Auth.getUserInfo === 'function'
        ? window.Auth.getUserInfo()
        : null
      if (!user) return false
      const userId = user.user_id ?? user.id ?? user.UserId ?? user.UserID
      const creatorId = record.created_by_user_id ?? record.creator_user_id ?? record.user_id
      if (userId != null && creatorId != null) return String(userId) === String(creatorId)
      const username = user.username ?? user.UserName ?? user.name
      const creatorName = record.created_by_name ?? record.creator_name ?? record.username
      return Boolean(username && creatorName && String(username).toLocaleLowerCase('tr-TR') === String(creatorName).toLocaleLowerCase('tr-TR'))
    }

    renderPagination() {
      const label = document.getElementById('manualRecordsPageInfo')
      const previous = document.getElementById('manualRecordsPrevious')
      const next = document.getElementById('manualRecordsNext')
      const start = this.total ? this.offset + 1 : 0
      const end = Math.min(this.offset + this.limit, this.total)
      if (label) label.textContent = `${start}-${end} / ${this.total}`
      if (previous) previous.disabled = this.offset <= 0
      if (next) next.disabled = this.offset + this.limit >= this.total
    }

    changePage(direction) {
      const nextOffset = Math.max(0, this.offset + (direction * this.limit))
      if (nextOffset === this.offset || nextOffset >= this.total) return
      this.offset = nextOffset
      this.loadRecords()
    }

    async openDetail(recordId, editing) {
      try {
        const response = await this.request(`/api/v1/manual-records/${encodeURIComponent(recordId)}`, { headers: { Accept: 'application/json' } })
        const payload = await parseResponse(response)
        if (!response.ok) throw new Error(errorMessage(response.status, payload))
        const record = unwrapRecord(payload)
        this.detailRecord = record
        const values = recordValues(record)
        document.getElementById('manualRecordDetailTitle').textContent = `${editing ? 'Manuel Kaydı Düzenle' : 'Manuel Kayıt Detayı'} #${recordId}`
        const time = document.getElementById('manualRecordEditTime')
        time.value = isoToLocalDateTimeValue(record.time || record.record_time)
        time.disabled = !editing
        time.dataset.original = time.value
        const fields = document.getElementById('manualRecordEditFields')
        const tagMap = new Map((this.form && this.form.tags || []).map(tag => [tag.name, tag]))
        fields.innerHTML = Object.entries(values).map(([name, value], index) => {
          const tag = tagMap.get(name) || { name, data_type: typeof value === 'boolean' ? 'Bool' : (typeof value === 'number' ? 'Double' : 'String') }
          const type = tag.data_type
          const control = type === 'Bool'
            ? `<select class="form-select manual-edit-value" data-name="${escapeHtml(name)}" data-type="Bool" ${editing ? '' : 'disabled'}><option value="true" ${value === true ? 'selected' : ''}>true</option><option value="false" ${value === false ? 'selected' : ''}>false</option></select>`
            : `<input class="form-control manual-edit-value" data-name="${escapeHtml(name)}" data-type="${escapeHtml(type)}" type="${NUMERIC_TYPES.has(type) ? 'number' : 'text'}" ${NUMERIC_TYPES.has(type) ? 'step="any"' : ''} value="${escapeHtml(value)}" ${editing ? '' : 'disabled'}>`
          return `<div class="col-md-6"><label class="form-label">${escapeHtml(name)} <small class="text-white-50">(${escapeHtml(type)})</small></label><div class="input-group">${control}${tag.unit ? `<span class="input-group-text">${escapeHtml(tag.unit)}</span>` : ''}</div></div>`
        }).join('') || '<div class="text-white-50">Bu kayıtta tag değeri yok.</div>'
        document.getElementById('manualRecordEditSave').style.display = editing ? '' : 'none'
        const meta = document.getElementById('manualRecordDetailMeta')
        meta.textContent = `Oluşturan: ${record.created_by_name || record.created_by_user_id || '—'} • Oluşturulma: ${record.created_at || '—'} • Revision: ${record.revision ?? '—'}`
        new coreui.Modal(document.getElementById('manualRecordDetailModal')).show()
      } catch (error) {
        this.showToast('Manuel Kayıt', error.message, 'danger')
      }
    }

    async saveEdit() {
      if (!this.detailRecord) return
      const recordId = getRecordId(this.detailRecord)
      const values = {}
      try {
        document.querySelectorAll('#manualRecordEditFields .manual-edit-value').forEach(input => {
          values[input.dataset.name] = coerceValue(input.dataset.type, input.value)
        })
        const body = { values }
        const time = document.getElementById('manualRecordEditTime')
        if (time.value !== time.dataset.original) body.time = localDateTimeToOffsetIso(time.value)
        const response = await this.request(`/api/v1/manual-records/${encodeURIComponent(recordId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
        const payload = await parseResponse(response)
        if (!response.ok) throw new Error(errorMessage(response.status, payload))
        const modal = coreui.Modal.getInstance(document.getElementById('manualRecordDetailModal'))
        if (modal) modal.hide()
        this.showToast('Manuel Kayıt', 'Kayıt güncellendi.', 'success')
        await this.loadRecords()
        this.onLiveRefresh(this.form.form_id)
      } catch (error) {
        this.showToast('Manuel Kayıt', error.message, 'danger')
      }
    }

    async deleteRecord(recordId) {
      if (!window.confirm('Bu manuel kayıt silinsin mi? Kayıt backend üzerinde soft-delete edilecektir.')) return
      try {
        const response = await this.request(`/api/v1/manual-records/${encodeURIComponent(recordId)}`, { method: 'DELETE', headers: { Accept: 'application/json' } })
        const payload = await parseResponse(response)
        if (!response.ok) throw new Error(errorMessage(response.status, payload))
        this.showToast('Manuel Kayıt', 'Kayıt silindi.', 'success')
        if (this.offset > 0 && this.records.length === 1) this.offset = Math.max(0, this.offset - this.limit)
        await this.loadRecords()
      } catch (error) {
        this.showToast('Manuel Kayıt', error.message, 'danger')
      }
    }
  }

  return Object.freeze({
    INTEGER_TYPES,
    NUMERIC_TYPES,
    SUPPORTED_TYPES,
    IdempotencyTracker,
    RecordsController,
    coerceValue,
    createIdempotencyKey,
    errorMessage,
    isManualForm,
    prepareImportForm,
    isoToLocalDateTimeValue,
    localDateTimeToOffsetIso,
    localDateTimeValue,
    latestRecord,
    normalizeListResponse,
    recordValues,
    stableStringify
  })
})
